import { Popover, Typography } from "@material-ui/core";
import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import React from "react";
import { ICardElementWithChildren } from "backend/model/model";
import { useClickAway } from "react-use";

const useStyles = makeThemedStyles("DepartmentPopover", (theme: any) => ({
  root: {
    background: "#FFFFFF",
    borderRadius: "1.6em",
    padding: "15px 15px 15px 15px",
  },
  paper: {
    fontSize: "1.142em",
    padding: 0,
    borderRadius: "1.6em",
    width: "29.3em",
    overflowX: "unset",
    overflowY: "unset",
    boxShadow: "0px 3px 6px rgba(0,0,0,0.2)",
  },
  items: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "20px",
    padding: "0px 10px 0px 10px",
  },
  item: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "10px",
    justifyContent: "flex-start",
    alignItems: "center",
    cursor: "pointer",
    "&:hover": {
      color: "blue",
    },
  },
  title: {
    fontSize: "1.2em",
    lineHeight: "1.66em",
    marginBottom: ".8em",
    letterSpacing: "0.05625px",
    color: "rgba(101, 106, 114, 1)",
    textTransform: "uppercase",
  },
  ranges: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rangeColor: {
    fontSize: "1.2em",
    marginTop: "0.33em",
    lineHeight: "1.25em",
    borderRadius: "1.66em",
    height: "1.916em",
    minWidth: "2.416em",
    boxShadow: "0px 3px 5px rgba(16, 41, 117, 0.08)",
    padding: ".33em 1em",
    background: "rgba(221, 81, 70, 1)",
    color: "rgb(255, 255,255)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rangeTitle: {
    margin: 0,
    fontSize: "1.2em",
    lineHeight: "1.25em",
    color: "rgba(101, 106, 114, 1)",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  range: {
    margin: "0 1.4em",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center",
  },
  icon: {
    width: "35px",
    height: "35px",
  },
  depTitle: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    color: "rgba(101, 106, 114, 1)",
    fontSize: "12px",
  },
}));

function PopoverContent({
  elements,
  onSelect,
  imageUrl,
}: {
  imageUrl?: string;
  elements: ICardElementWithChildren[];
  onSelect?: (item: ICardElementWithChildren) => void;
}) {
  const classes = useStyles();
  return (
    <div className={classes.root}>
      <div className={classes.items}>
        {elements.map((item, index) => (
          <div
            key={index}
            className={classes.item}
            onClick={() => {
              if (onSelect) onSelect(item);
            }}
          >
            <div>
              {imageUrl ? (
                <img className={classes.icon} src={imageUrl} />
              ) : null}
            </div>
            <div className={classes.depTitle}>{item.name}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const DepartmentPopover = (
  props: React.PropsWithChildren<{
    items: ICardElementWithChildren[];
    typenames: string[];
    isOpen?: boolean;
    anchor?: HTMLButtonElement | HTMLDivElement | null;
    onClose?: () => void;
    onSelect?: (item: ICardElementWithChildren) => void;
    rtl?: boolean;
    departmentIcon?: string;
  }>
) => {
  const classes = useStyles();
  const ref = React.useRef<HTMLElement | null>(null);
  useClickAway(ref, () => {
    if (props.onClose) props.onClose();
  });

  return (
    <Popover
      ref={ref}
      open={!!props.isOpen}
      anchorEl={props.anchor}
      onClose={props.onClose}
      classes={{ paper: classes.paper }}
      anchorOrigin={{
        vertical: "center",
        horizontal: props.rtl ? "left" : "right",
      }}
      transformOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      dir={props.rtl ? "rtl" : "ltr"}
    >
      <PopoverContent
        elements={props.items}
        onSelect={props.onSelect}
        imageUrl={props.departmentIcon}
      />
    </Popover>
  );
};

export default DepartmentPopover;
